import { useMutation } from "@apollo/client/react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Head } from "../../../../../components/core";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../../contexts/NotifyContext";
import { VehicleBasic } from "../../../../../graphql/fleets/vehicles";
import {
  ADD_VEHICLEISSUE,
  GET_VEHICLEISSUE_BY_VEHICLE_ID,
  VehicleIssueForm,
} from "../../../../../graphql/fleets/vehicles/pages/issues";
import Form from "../../components/IssuesForm";

const IssuesCreate = ({
  breadcrumbs,
  vehicleBasic,
}: {
  breadcrumbs: Breadcrumb[];
  vehicleBasic: VehicleBasic | null;
}) => {
  let { vehicleId } = useParams();
  const { t } = useTranslation();
  const { addNotify } = useNotifyContext();
  const navigate = useNavigate();

  const [createVehicleIssue] = useMutation(ADD_VEHICLEISSUE, {
    refetchQueries: [
      { query: GET_VEHICLEISSUE_BY_VEHICLE_ID, variables: { vehicleId } },
    ],
  });
  const handleCreate = (
    values: VehicleIssueForm,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    if (!vehicleId) return;
    createVehicleIssue({
      variables: {
        vehicleId: parseInt(vehicleId),
        reportedDate: values.reportedDate,
        summary: values.summary,
        issueType: values.issueType,
        primaryMeter: values.primaryMeter,
        reportedById: values.reportedById,
        assignedToId: values.assignedToId,
        priority: values.priority,
        overDueDate: values.overDueDate,
        description: values.description,
        status: values.status,
        statusNotes: values.statusNotes,
      },
    })
      .then(async ({ data }) => {
        if (data?.vehicleIssueCreate) {
          return navigate(
            `/fleets/vehicles/${vehicleId}/issue/${data.vehicleIssueCreate.vehicleIssue.id}`
          );
        } else {
          toast.error("Something went wrong, please try again later");
        }
        actions.setSubmitting(false);
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "Issues create failed",
          message: error.message,
        });
      });
  };

  return (
    <>
      <Head
        title={IssuesCreateResource.name}
        heading={IssuesCreateResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: IssuesCreateResource.name,
            href: null,
          },
        ]}
      />
      <div className="mx-auto max-w-4xl">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_vehicle_issues_create")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_vehicle_issues_create")}
              </p>
            </div>
          </div>

          <Form
            initialValues={{
              vehicleId: Number(vehicleId),
              reportedDate: null,
              summary: "",
              issueType: "",
              primaryMeter: null,
              reportedById: null,
              assignedToId: null,
              priority: null,
              overDueDate: null,
              description: "",
              status: 0,
              statusNotes: "",
            }}
            onSubmit={handleCreate}
            submitLabel={t("text_create")}
            onCancel={() => {
              navigate(`/fleets/vehicles/${vehicleId}/issue`);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default IssuesCreate;
export const IssuesCreateResource: ResourceProps = {
  name: "Issues",
  description: "Update an existing vehicle issues",
  access: ["update-vehicles"],
  path: "/issue/add",
  icon: "bi bi-person",
};
